import { queryRequest } from './client';
import { getMixedContents, getMixedOverview, getOverview } from './queries';
import MixedCollection from '../models/MixedCollection';
import Overview from '../models/Overview';
import Tab from '../models/Tab';
import mixedFetchBuilder from './gqlHelper';
import { isEmpty, parseConfig } from 'util/index';
import graphql from 'api/graphql';
import { TAB_TYPES } from 'globalConst/const';
import { contentActions } from 'actions/actions-const';
import { getStore } from 'store';
import logger from 'util/logger';

const store = getStore();

/**
 * Fetch a overview collection from the universal api
 * @param {string} slug
 * @param contentTypeId
 * @returns {Object} Overview
 */

export const fetchOverview = async ({ slug, contentTypeId }) => {
  const { data } =
    (await queryRequest({
      query: getOverview,
      variables: { slug },
    })) || {};

  // Expose the config of a ref item
  if (data.overview && data.overview.items) {
    data.overview.items = await Promise.all(
      data.overview.items.map(async (item) => {
        const config = parseConfig(item.config);

        // if a overview has item(s) that contain tabs then we fetch the content of that tab here
        item.hasTabs =
          !isEmpty(item.refs) &&
          !isEmpty(item.options) &&
          item.options.some((option) => option === TAB_TYPES.INDEXABLE || option === TAB_TYPES.ANCHOR);

        if (item.hasTabs) {
          try {
            const { data } =
              (await queryRequest({
                query: getOverview,
                variables: { slug: item.refs[0].uri },
              })) || {};

            item.tabs = data.overview.items.map((tab) => new Tab(tab));
          } catch (error) {
            logger.error(`Something went wrong while fetching the data for the tabs:`, error);
          }
        }

        /**
         * If there is a config with key Experiment present then the ref items must have a config with a variant
         * We need this variant config to determine what variant c.q. ref needs to be fetched.
         * Here we are fetching the config of the ref and add this to the data
         */
        if (config.Experiment && !isEmpty(item.refs)) {
          item.refs = await Promise.all(
            item.refs.map(async (ref) => {
              try {
                ref.config = await graphql.fetchCollectionConfig({
                  slug: ref.uri,
                });
              } catch (error) {
                logger.error(`Error ${error} while fetchCollectionConfig with graphQL for ${ref.uri}`);
              }
              return ref;
            })
          );
        }
        return item;
      })
    );
  }

  const state = store.getState();
  const { pageSlug } = state.content || {};

  /**
   * Here we overwrite the page title and page description in the redux store
   * with the title and description from the overview collection.
   */
  if (contentTypeId === 'overview') {
    store.dispatch({
      type: contentActions.UPDATE_PAGE_CONTENT,
      pageSlug,
      title: data.overview.title,
      description: data.overview.description,
    });
  }

  return new Overview(data.overview);
};

/**
 * Fetch mixed content from the universal api
 * @param {string} slug
 * @returns {Object} Mixed
 */
export const fetchMixed = async ({ slug }) => {
  // fetch data from overview collection on universal API
  const { data } =
    (await queryRequest({
      query: getMixedOverview,
      variables: { slug },
    })) || {};
  const searchItems = data.mixed && data.mixed.items[0].refs;

  // Then use data to build most popular or most recent query
  const mixedCall = searchItems.reduce(
    (acc, item, i) => `${acc} ${mixedFetchBuilder(item.uri, item.targetType, i)}`,
    ''
  );
  // Call for data from universal API
  const { data: mixedContents } = (await queryRequest({ query: getMixedContents(mixedCall) })) || {};
  return new MixedCollection(mixedContents);
};
